function getFocusableElements(container) {
  if (!container || typeof container.querySelectorAll !== 'function') return [];
  return Array.from(
    container.querySelectorAll(
      "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), [draggable], area, input:not([type=hidden]):enabled, select:enabled, textarea:enabled, object, iframe"
    )
  );
}


class SectionId {
  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section id (e.g. 'template--22224696705326')
  static parseId(qualifiedSectionId) {
    return qualifiedSectionId.split('__')[0];
  }

  // for a qualified section id (e.g. 'template--22224696705326__main'), return just the section name (e.g. 'main')
  static parseSectionName(qualifiedSectionId) {
    return qualifiedSectionId.split('__')[1];
  }

  // for a section id (e.g. 'template--22224696705326') and a section name (e.g. 'recommended-products'), return a qualified section id (e.g. 'template--22224696705326__recommended-products')
  static getIdForSection(sectionId, sectionName) {
    return `${sectionId}__${sectionName}`;
  }
}

class HTMLUpdateUtility {
  /**
   * Used to swap an HTML node with a new node.
   * The new node is inserted as a previous sibling to the old node, the old node is hidden, and then the old node is removed.
   *
   * The function currently uses a double buffer approach, but this should be replaced by a view transition once it is more widely supported https://developer.mozilla.org/en-US/docs/Web/API/View_Transitions_API
   */
  static viewTransition(oldNode, newContent, preProcessCallbacks = [], postProcessCallbacks = []) {
    preProcessCallbacks?.forEach((callback) => callback(newContent));

    const newNodeWrapper = document.createElement('div');
    HTMLUpdateUtility.setInnerHTML(newNodeWrapper, newContent.outerHTML);
    const newNode = newNodeWrapper.firstChild;

    // dedupe IDs
    const uniqueKey = Date.now();
    oldNode.querySelectorAll('[id], [form]').forEach((element) => {
      element.id && (element.id = `${element.id}-${uniqueKey}`);
      element.form && element.setAttribute('form', `${element.form.getAttribute('id')}-${uniqueKey}`);
    });

    oldNode.parentNode.insertBefore(newNode, oldNode);
    oldNode.classList.add('hidden');

    postProcessCallbacks?.forEach((callback) => callback(newNode));

    setTimeout(() => oldNode.remove(), 500);
  }

  // Sets inner HTML and reinjects the script tags to allow execution. By default, scripts are disabled when using element.innerHTML.
  static setInnerHTML(element, html) {
    element.innerHTML = html;
    element.querySelectorAll('script').forEach((oldScriptTag) => {
      const newScriptTag = document.createElement('script');
      Array.from(oldScriptTag.attributes).forEach((attribute) => {
        newScriptTag.setAttribute(attribute.name, attribute.value);
      });
      newScriptTag.appendChild(document.createTextNode(oldScriptTag.innerHTML));
      oldScriptTag.parentNode.replaceChild(newScriptTag, oldScriptTag);
    });
  }
}

// CRITICAL FIX: Defer DOM queries to reduce TBT - initialize after page is interactive
(function initDetailsSummaries() {
  function setupDetailsSummaries() {
    document.querySelectorAll('[id^="Details-"] summary').forEach((summary) => {
      const details = summary.closest('details');
      if (!details) return; // Only apply to native details elements

      summary.setAttribute('role', 'button');
      summary.setAttribute('aria-expanded', details.hasAttribute('open'));

      if (summary.nextElementSibling && summary.nextElementSibling.getAttribute('id')) {
        summary.setAttribute('aria-controls', summary.nextElementSibling.id);
      }

      summary.addEventListener('click', (event) => {
        const details = event.currentTarget.closest('details');
        if (details) {
          event.currentTarget.setAttribute('aria-expanded', !details.hasAttribute('open'));
        }
      });

      if (summary.closest('header-drawer, menu-drawer')) return;
      summary.parentElement.addEventListener('keyup', onKeyUpEscape);
    });
  }

  // Defer initialization to reduce TBT
  if (document.readyState === 'loading') {
    if ('requestIdleCallback' in window) {
      document.addEventListener('DOMContentLoaded', function () {
        requestIdleCallback(setupDetailsSummaries, { timeout: 1000 });
      });
    } else {
      document.addEventListener('DOMContentLoaded', function () {
        setTimeout(setupDetailsSummaries, 1000);
      });
    }
  } else {
    // DOM already loaded
    if ('requestIdleCallback' in window) {
      requestIdleCallback(setupDetailsSummaries, { timeout: 1000 });
    } else {
      setTimeout(setupDetailsSummaries, 1000);
    }
  }
})();

const trapFocusHandlers = {};

function trapFocus(container, elementToFocus = container) {
  removeTrapFocus();
  if (!container || typeof container.focus !== 'function') return;

  var elements = getFocusableElements(container);
  var first = elements[0] || container;
  var last = elements[elements.length - 1] || container;
  if (!elements.length && !container.hasAttribute('tabindex')) container.setAttribute('tabindex', '-1');
  if (!elementToFocus || typeof elementToFocus.focus !== 'function') elementToFocus = first;

  trapFocusHandlers.focusin = (event) => {
    if (event.target !== container && event.target !== last && event.target !== first) return;

    document.addEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.focusout = function () {
    document.removeEventListener('keydown', trapFocusHandlers.keydown);
  };

  trapFocusHandlers.keydown = function (event) {
    if (event.code.toUpperCase() !== 'TAB') return; // If not TAB key
    // On the last focusable element and tab forward, focus the first element.
    if (event.target === last && !event.shiftKey) {
      event.preventDefault();
      first.focus();
    }

    //  On the first focusable element and tab backward, focus the last element.
    if ((event.target === container || event.target === first) && event.shiftKey) {
      event.preventDefault();
      last.focus();
    }
  };

  document.addEventListener('focusout', trapFocusHandlers.focusout);
  document.addEventListener('focusin', trapFocusHandlers.focusin);

  elementToFocus.focus();

  if (
    elementToFocus.tagName === 'INPUT' &&
    ['search', 'text', 'email', 'url'].includes(elementToFocus.type) &&
    elementToFocus.value
  ) {
    elementToFocus.setSelectionRange(0, elementToFocus.value.length);
  }
}

// Here run the querySelector to figure out if the browser supports :focus-visible or not and run code based on it.
try {
  document.querySelector(':focus-visible');
} catch (e) {
  focusVisiblePolyfill();
}

function focusVisiblePolyfill() {
  const navKeys = [
    'ARROWUP',
    'ARROWDOWN',
    'ARROWLEFT',
    'ARROWRIGHT',
    'TAB',
    'ENTER',
    'SPACE',
    'ESCAPE',
    'HOME',
    'END',
    'PAGEUP',
    'PAGEDOWN',
  ];
  let currentFocusedElement = null;
  let mouseClick = null;

  window.addEventListener('keydown', (event) => {
    if (navKeys.includes(event.code.toUpperCase())) {
      mouseClick = false;
    }
  });

  window.addEventListener('mousedown', (event) => {
    mouseClick = true;
  });

  window.addEventListener(
    'focus',
    () => {
      if (currentFocusedElement) currentFocusedElement.classList.remove('focused');

      if (mouseClick) return;

      currentFocusedElement = document.activeElement;
      currentFocusedElement.classList.add('focused');
    },
    true
  );
}

function pauseAllMedia() {
  document.querySelectorAll('.js-youtube').forEach((video) => {
    video.contentWindow.postMessage('{"event":"command","func":"' + 'pauseVideo' + '","args":""}', '*');
  });
  document.querySelectorAll('.js-vimeo').forEach((video) => {
    video.contentWindow.postMessage('{"method":"pause"}', '*');
  });
  document.querySelectorAll('video').forEach((video) => video.pause());
  document.querySelectorAll('video.card_video').forEach((card_video) => card_video.play());
  document.querySelectorAll('product-model').forEach((model) => {
    if (model.modelViewerUI) model.modelViewerUI.pause();
  });
}

function removeTrapFocus(elementToFocus = null) {
  document.removeEventListener('focusin', trapFocusHandlers.focusin);
  document.removeEventListener('focusout', trapFocusHandlers.focusout);
  document.removeEventListener('keydown', trapFocusHandlers.keydown);

  if (elementToFocus && typeof elementToFocus.focus === 'function' && elementToFocus.isConnected !== false) elementToFocus.focus();
}

function onKeyUpEscape(event) {
  if (event.code.toUpperCase() !== 'ESCAPE') return;

  const openDetailsElement = event.target.closest('details[open]');
  if (!openDetailsElement) return;

  const summaryElement = openDetailsElement.querySelector('summary');
  openDetailsElement.removeAttribute('open');
  summaryElement.setAttribute('aria-expanded', false);
  summaryElement.focus();
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.quantityUpdateUnsubscriber = undefined;
    this.input = this.querySelector('input');
    if (!this.input) return;
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this))
    );
  }


  connectedCallback() {
    if (!this.input) return;
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    if (!this.input) return;
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      if (buttonMinus) buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      if (buttonPlus) buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

customElements.define('quantity-input', QuantityInput);

function debounce(fn, wait) {
  let t;
  return (...args) => {
    clearTimeout(t);
    t = setTimeout(() => fn.apply(this, args), wait);
  };
}

function throttle(fn, delay) {
  let lastCall = 0;
  return function (...args) {
    const now = new Date().getTime();
    if (now - lastCall < delay) {
      return;
    }
    lastCall = now;
    return fn(...args);
  };
}

function fetchConfig(type = 'json') {
  return {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: `application/${type}` },
  };
}

/*
 * Shopify Common JS
 *
 */
if (typeof window.Shopify == 'undefined') {
  window.Shopify = {};
}

Shopify.bind = function (fn, scope) {
  return function () {
    return fn.apply(scope, arguments);
  };
};

Shopify.setSelectorByValue = function (selector, value) {
  for (var i = 0, count = selector.options.length; i < count; i++) {
    var option = selector.options[i];
    if (value == option.value || value == option.innerHTML) {
      selector.selectedIndex = i;
      return i;
    }
  }
};

Shopify.addListener = function (target, eventName, callback) {
  target.addEventListener
    ? target.addEventListener(eventName, callback, false)
    : target.attachEvent('on' + eventName, callback);
};

Shopify.postLink = function (path, options) {
  options = options || {};
  var method = options['method'] || 'post';
  var params = options['parameters'] || {};

  var form = document.createElement('form');
  form.setAttribute('method', method);
  form.setAttribute('action', path);

  for (var key in params) {
    var hiddenField = document.createElement('input');
    hiddenField.setAttribute('type', 'hidden');
    hiddenField.setAttribute('name', key);
    hiddenField.setAttribute('value', params[key]);
    form.appendChild(hiddenField);
  }
  document.body.appendChild(form);
  form.submit();
  document.body.removeChild(form);
};

Shopify.CountryProvinceSelector = function (country_domid, province_domid, options) {
  this.countryEl = document.getElementById(country_domid);
  this.provinceEl = document.getElementById(province_domid);
  this.provinceContainer = document.getElementById(options['hideElement'] || province_domid);

  Shopify.addListener(this.countryEl, 'change', Shopify.bind(this.countryHandler, this));

  this.initCountry();
  this.initProvince();
};

Shopify.CountryProvinceSelector.prototype = {
  initCountry: function () {
    var value = this.countryEl.getAttribute('data-default');
    Shopify.setSelectorByValue(this.countryEl, value);
    this.countryHandler();
  },

  initProvince: function () {
    var value = this.provinceEl.getAttribute('data-default');
    if (value && this.provinceEl.options.length > 0) {
      Shopify.setSelectorByValue(this.provinceEl, value);
    }
  },

  countryHandler: function (e) {
    var opt = this.countryEl.options[this.countryEl.selectedIndex];
    var raw = opt.getAttribute('data-provinces');
    var provinces = JSON.parse(raw);

    this.clearOptions(this.provinceEl);
    if (provinces && provinces.length == 0) {
      this.provinceContainer.classList.add('hidden');
    } else {
      for (var i = 0; i < provinces.length; i++) {
        var opt = document.createElement('option');
        opt.value = provinces[i][0];
        opt.innerHTML = provinces[i][1];
        this.provinceEl.appendChild(opt);
      }

      this.provinceContainer.classList.remove('hidden');
    }
  },

  clearOptions: function (selector) {
    while (selector.firstChild) {
      selector.removeChild(selector.firstChild);
    }
  },

  setOptions: function (selector, values) {
    for (var i = 0, count = values.length; i < values.length; i++) {
      var opt = document.createElement('option');
      opt.value = values[i];
      opt.innerHTML = values[i];
      selector.appendChild(opt);
    }
  },
};

class MenuDrawer extends HTMLElement {
  constructor() {
    super();

    this.mainDetailsToggle = this.querySelector('details');

    this.addEventListener('keyup', this.onKeyUp.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.onMenuRouteIntentHandler = this.onMenuRouteIntent.bind(this);
    this.bindEvents();
  }

  bindEvents() {
    this.querySelectorAll('summary').forEach((summary) =>
      summary.addEventListener('click', this.onSummaryClick.bind(this))
    );
    this.querySelectorAll(
      'button:not(.localization-selector):not(.country-selector__close-button):not(.country-filter__reset-button):not(.menu-drawer__close-button-top)'
    ).forEach((button) => button.addEventListener('click', this.onCloseButtonClick.bind(this)));
    // Handle top-level close button - closes the main drawer
    const topCloseButton = this.querySelector('.menu-drawer__close-button-top');
    if (topCloseButton) {
      topCloseButton.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        if (this.mainDetailsToggle) {
          this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
        }
      });
    }

    const menuDrawer = this.getMenuDrawerPanel();
    if (menuDrawer && !menuDrawer.dataset.routeFeedbackBound) {
      menuDrawer.dataset.routeFeedbackBound = 'true';
      menuDrawer.addEventListener('pointerdown', this.onMenuRouteIntentHandler);
      menuDrawer.addEventListener('click', this.onMenuRouteIntentHandler);
      menuDrawer.addEventListener('keydown', this.onMenuRouteIntentHandler);
    }
  }

  getMenuDrawerPanel() {
    return this.drawerPanel || this.querySelector('.menu-drawer') || document.getElementById('menu-drawer');
  }

  cancelMenuDrawerClose() {
    if (this.menuDrawerCloseCancel) {
      this.menuDrawerCloseCancel();
      this.menuDrawerCloseCancel = null;
    }
  }

  afterMenuDrawerClose(menuDrawer, done) {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    if (!menuDrawer || reducedMotion) {
      done();
      return;
    }

    let closeTimer = null;
    const finish = () => {
      if (this.menuDrawerCloseCancel !== cancel) return;
      cancel();
      this.menuDrawerCloseCancel = null;
      done();
    };
    const onTransitionEnd = (event) => {
      if (event.target === menuDrawer && event.propertyName === 'transform') finish();
    };
    const cancel = () => {
      menuDrawer.removeEventListener('transitionend', onTransitionEnd);
      if (closeTimer) clearTimeout(closeTimer);
      closeTimer = null;
    };

    this.menuDrawerCloseCancel = cancel;
    menuDrawer.addEventListener('transitionend', onTransitionEnd);
    closeTimer = setTimeout(finish, 320);
  }

  showMenuCollectionSkeleton(url) {
    const existingSkeleton = document.querySelector('.menu-collection-skeleton');
    if (existingSkeleton) return;

    const isLatestOffers = url && url.pathname.replace(/\/$/, '') === '/pages/latest-offers';
    const skeleton = document.createElement('div');
    skeleton.className = `menu-collection-skeleton${isLatestOffers ? ' menu-collection-skeleton--latest-offers' : ''}`;
    skeleton.setAttribute('aria-hidden', 'true');
    skeleton.innerHTML = isLatestOffers ? `
      <div class="menu-collection-skeleton__latest-intro">
        <span class="menu-collection-skeleton__latest-heading menu-collection-skeleton__shimmer"></span>
      </div>
      <div class="menu-collection-skeleton__latest-grid">
        <span class="menu-collection-skeleton__latest-card menu-collection-skeleton__shimmer"></span>
        <span class="menu-collection-skeleton__latest-card menu-collection-skeleton__shimmer"></span>
        <span class="menu-collection-skeleton__latest-card menu-collection-skeleton__shimmer"></span>
        <span class="menu-collection-skeleton__latest-card menu-collection-skeleton__shimmer"></span>
      </div>
    ` : `
      <div class="menu-collection-skeleton__hero menu-collection-skeleton__shimmer"></div>
      <div class="menu-collection-skeleton__inner">
        <div class="menu-collection-skeleton__tabs">
          <span class="menu-collection-skeleton__tab menu-collection-skeleton__shimmer"></span>
          <span class="menu-collection-skeleton__tab menu-collection-skeleton__shimmer"></span>
          <span class="menu-collection-skeleton__tab menu-collection-skeleton__shimmer"></span>
        </div>
        <div class="menu-collection-skeleton__sort menu-collection-skeleton__shimmer"></div>
        <div class="menu-collection-skeleton__grid">
          <span class="menu-collection-skeleton__card menu-collection-skeleton__shimmer"></span>
          <span class="menu-collection-skeleton__card menu-collection-skeleton__shimmer"></span>
          <span class="menu-collection-skeleton__card menu-collection-skeleton__shimmer"></span>
          <span class="menu-collection-skeleton__card menu-collection-skeleton__shimmer"></span>
        </div>
      </div>
    `;
    document.body.appendChild(skeleton);

    if (this.menuCollectionSkeletonTimer) clearTimeout(this.menuCollectionSkeletonTimer);
    this.menuCollectionSkeletonTimer = setTimeout(() => {
      skeleton.remove();
      this.menuCollectionSkeletonTimer = null;
    }, 6000);
  }

  setMenuRoutePending(url) {
    document.documentElement.classList.add('menu-route-pending');

    if (url) {
      if (url.pathname.replace(/\/$/, '') === '/pages/latest-offers') {
        document.documentElement.classList.add('menu-latest-offers-pending');
      }
      this.showMenuCollectionSkeleton(url);
      try {
        if (url.pathname.indexOf('/collections/') === 0) {
          sessionStorage.setItem('dt:menuCollectionEnter', '1');
        }
      } catch (e) {}
    }

    if (this.menuRoutePendingTimer) clearTimeout(this.menuRoutePendingTimer);
    this.menuRoutePendingTimer = setTimeout(() => {
      document.documentElement.classList.remove('menu-route-pending', 'menu-latest-offers-pending');
      this.menuRoutePendingTimer = null;
    }, 6000);
  }

  navigateAfterMenuClose(url) {
    if (this.menuRouteNavigationTimer) clearTimeout(this.menuRouteNavigationTimer);

    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.menuRouteNavigationTimer = setTimeout(() => {
      window.location.assign(url.href);
    }, reducedMotion ? 0 : 180);
  }

  getMenuRouteUrl(anchor, event) {
    if (!anchor || !anchor.href) return null;
    if (anchor.hasAttribute('download')) return null;
    if (anchor.target && anchor.target !== '_self') return null;
    if (event && (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)) return null;

    let url;
    try {
      url = new URL(anchor.href, window.location.href);
    } catch (e) {
      return null;
    }

    if (url.origin !== window.location.origin) return null;
    if (url.pathname === window.location.pathname && url.search === window.location.search && url.hash) return null;

    return url;
  }

  onMenuRouteIntent(event) {
    if (event.type === 'keydown' && event.key !== 'Enter') return;
    if (event.type !== 'keydown' && event.button !== undefined && event.button !== 0) return;
    if (event.type !== 'pointerdown' && event.defaultPrevented) return;

    const anchor = event.target && event.target.closest ? event.target.closest('a[href]') : null;
    const url = this.getMenuRouteUrl(anchor, event);
    if (!url) return;
    const isLatestOffers = url.pathname.replace(/\/$/, '') === '/pages/latest-offers';

    this.setMenuRoutePending(url);
    if (event.type !== 'pointerdown') {
      event.preventDefault();
    }
    if (event.type !== 'pointerdown' && isLatestOffers) {
      window.location.assign(url.href);
    } else if (event.type !== 'pointerdown' && this.mainDetailsToggle && this.mainDetailsToggle.hasAttribute('open')) {
      this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'));
      this.navigateAfterMenuClose(url);
    } else if (event.type !== 'pointerdown') {
      this.navigateAfterMenuClose(url);
    }
  }

  onKeyUp(event) {
    if (event.code.toUpperCase() !== 'ESCAPE') return;

    const openDetailsElement = event.target.closest('details[open]');
    if (!openDetailsElement) return;

    openDetailsElement === this.mainDetailsToggle
      ? this.closeMenuDrawer(event, this.mainDetailsToggle.querySelector('summary'))
      : this.closeSubmenu(openDetailsElement);
  }

  onSummaryClick(event) {
    const summaryElement = event.currentTarget;
    const detailsElement = summaryElement.parentNode;
    const parentMenuElement = detailsElement.closest('.has-submenu');
    const isOpen = detailsElement.hasAttribute('open');
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

    function addTrapFocus() {
      trapFocus(summaryElement.nextElementSibling, detailsElement.querySelector('button'));
      summaryElement.nextElementSibling.removeEventListener('transitionend', addTrapFocus);
    }

    if (detailsElement === this.mainDetailsToggle) {
      if (isOpen) event.preventDefault();
      isOpen ? this.closeMenuDrawer(event, summaryElement) : this.openMenuDrawer(summaryElement);

      if (window.matchMedia('(max-width: 992px)')) {
        document.documentElement.style.setProperty('--viewport-height', `${window.innerHeight}px`);
      }
    } else {
      setTimeout(() => {
        detailsElement.classList.add('menu-opening');
        summaryElement.setAttribute('aria-expanded', true);

        !reducedMotion || reducedMotion.matches
          ? addTrapFocus()
          : summaryElement.nextElementSibling.addEventListener('transitionend', addTrapFocus);
      }, 100);
    }
  }

  openMenuDrawer(summaryElement) {
    this.cancelMenuDrawerClose();
    const menuDrawer = this.getMenuDrawerPanel();
    if (menuDrawer) menuDrawer.classList.remove('menu-closing');

    this.prefetchLatestOffersWhenIdle();

    requestAnimationFrame(() => {
      this.mainDetailsToggle.classList.add('menu-opening');
      if (menuDrawer) menuDrawer.classList.add('menu-opening');
    });
    summaryElement.setAttribute('aria-expanded', true);
    trapFocus(this.mainDetailsToggle, summaryElement);
    document.body.classList.add(`overflow-hidden-${this.dataset.breakpoint}`);
  }

  prefetchLatestOffersWhenIdle() {
    if (this.latestOffersPrefetchScheduled) return;
    if (window.location.pathname.replace(/\/$/, '') === '/pages/latest-offers') return;

    const connection = window.navigator && (
      window.navigator.connection || window.navigator.mozConnection || window.navigator.webkitConnection
    );
    if (connection && (connection.saveData || /(^|-)2g$/.test(connection.effectiveType || ''))) return;

    this.latestOffersPrefetchScheduled = true;
    const prefetch = () => {
      if (document.querySelector('link[data-dt-latest-offers-prefetch]')) return;
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.as = 'document';
      link.href = '/pages/latest-offers';
      link.setAttribute('data-dt-latest-offers-prefetch', '');
      document.head.appendChild(link);
    };

    window.requestAnimationFrame(prefetch);
  }

  closeMenuDrawer(event, elementToFocus = false) {
    if (event === undefined) return;

    this.cancelMenuDrawerClose();

    const menuDrawer = this.getMenuDrawerPanel();
    this.mainDetailsToggle.classList.remove('menu-opening');
    if (menuDrawer) {
      menuDrawer.classList.remove('menu-opening');
      menuDrawer.classList.add('menu-closing');
    }

    elementToFocus?.setAttribute('aria-expanded', false);

    this.afterMenuDrawerClose(menuDrawer, () => {
      if (this.mainDetailsToggle.hasAttribute('open')) {
        this.mainDetailsToggle.removeAttribute('open');
      }
      const resetNestedDetails = (details) => {
        if (details !== this.mainDetailsToggle) {
          details.removeAttribute('open');
          details.classList.remove('menu-opening');
        }
      };

      this.mainDetailsToggle.querySelectorAll('details').forEach(resetNestedDetails);
      if (menuDrawer) menuDrawer.querySelectorAll('details').forEach(resetNestedDetails);
      if (menuDrawer) menuDrawer.classList.remove('menu-closing');
      document.body.classList.remove(`overflow-hidden-${this.dataset.breakpoint}`);
      removeTrapFocus(elementToFocus);
      this.onMenuDrawerClosed(menuDrawer);
    });
  }

  onMenuDrawerClosed() {}

  onFocusOut() {
    setTimeout(() => {
      if (this.mainDetailsToggle.hasAttribute('open') && !this.mainDetailsToggle.contains(document.activeElement))
        this.closeMenuDrawer();
    });
  }

  onCloseButtonClick(event) {
    const detailsElement = event.currentTarget.closest('details');
    this.closeSubmenu(detailsElement);
  }

  closeSubmenu(detailsElement) {
    detailsElement.classList.remove('menu-opening');
    detailsElement.querySelector('summary').setAttribute('aria-expanded', false);
    removeTrapFocus(detailsElement.querySelector('summary'));
    this.closeAnimation(detailsElement);
  }

  closeAnimation(detailsElement) {
    // Main drawer close is handled by closeMenuDrawer; this path is for submenus.
    if (detailsElement === this.mainDetailsToggle) {
      return;
    }

    let animationStart;

    const handleAnimation = (time) => {
      if (animationStart === undefined) {
        animationStart = time;
      }

      const elapsedTime = time - animationStart;

      if (elapsedTime < 400) {
        window.requestAnimationFrame(handleAnimation);
      } else {
        detailsElement.removeAttribute('open');
        if (detailsElement.closest('details[open]')) {
          trapFocus(detailsElement.closest('details[open]'), detailsElement.querySelector('summary'));
        }
      }
    };

    window.requestAnimationFrame(handleAnimation);
  }
}

customElements.define('menu-drawer', MenuDrawer);

class HeaderDrawer extends HTMLElement {
  connectedCallback() {
    if (this.toggle) return;
    this.toggle = this.querySelector('.dt-nav-toggle');
    this.dialog = this.querySelector('.dt-nav-dialog');
    this.pane = this.querySelector('.dt-nav-pane');
    this.content = this.querySelector('.dt-nav-content');
    this.homeSlot = this.querySelector('.dt-nav-trigger-slot');
    this.openSlot = this.querySelector('.dt-nav-toggle-slot');
    this.menuLabel = this.toggle.getAttribute('aria-label');
    this.content.querySelectorAll('nav > .dt-nav-category-group, .dt-nav-links > li').forEach((item, index) => {
      item.setAttribute('data-nav-reveal', '');
      item.style.setProperty('--nav-delay', `${60 + Math.min(index, 5) * 16}ms`);
    });
    this.toggle.addEventListener('click', () => this.setOpen(!this.expanded));
    this.dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      this.setOpen(false);
    });
    this.dialog.addEventListener('click', (event) => {
      if (event.target === this.dialog) this.setOpen(false);
    });
    this.pane.addEventListener('transitionend', (event) => {
      if (event.target === this.pane && event.pseudoElement === '::before' && event.propertyName === 'opacity') this.finishTransition();
    });
    this.viewportWidth = window.innerWidth;
    this.onResize = () => {
      // Mobile browser chrome changes height without changing the menu layout.
      const width = window.innerWidth;
      if (width === this.viewportWidth) return;
      this.viewportWidth = width;
      if (this.expanded) this.setOpen(false);
    };
    window.addEventListener('resize', this.onResize);
  }

  disconnectedCallback() {
    clearTimeout(this.transitionTimer);
    window.removeEventListener('resize', this.onResize);
    if (this.dialog?.open) {
      this.dialog.close();
      document.body.style.overflow = this.previousOverflow;
    }
  }

  setOpen(expanded) {
    if (this.expanded === expanded) return;
    clearTimeout(this.transitionTimer);
    this.expanded = expanded;
    this.content.inert = true;

    if (expanded) this.querySelector('.dt-nav-category-group')?.removeAttribute('open');

    if (expanded && !this.dialog.open) {
      const rect = this.toggle.getBoundingClientRect();
      this.homeSlot.style.width = `${rect.width}px`;
      this.homeSlot.style.height = `${rect.height}px`;
      this.dialog.style.setProperty('--nav-left', `${rect.left}px`);
      this.dialog.style.setProperty('--nav-top', `${rect.top}px`);
      this.dialog.style.setProperty('--nav-button-width', `${rect.width}px`);
      this.dialog.style.setProperty('--nav-button-height', `${rect.height}px`);
      this.previousOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      this.dialog.showModal();
      this.openSlot.appendChild(this.toggle);
      // Establish the transparent starting state before revealing the glass.
      getComputedStyle(this.dialog, '::backdrop').opacity;
      getComputedStyle(this.pane, '::before').opacity;
    }

    this.toggle.setAttribute('aria-expanded', String(expanded));
    this.toggle.setAttribute('aria-label', expanded ? 'Close menu' : this.menuLabel);
    this.dialog.dataset.state = expanded ? 'opening' : 'closing';
    this.toggle.focus({ preventScroll: true });
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) this.finishTransition();
    else this.transitionTimer = setTimeout(() => this.finishTransition(), 470);
  }

  finishTransition() {
    clearTimeout(this.transitionTimer);
    if (this.expanded) {
      this.dialog.dataset.state = 'open';
      this.content.inert = false;
    } else {
      this.dialog.dataset.state = 'closed';
      this.dialog.close();
      this.homeSlot.appendChild(this.toggle);
      document.body.style.overflow = this.previousOverflow;
      this.toggle.focus({ preventScroll: true });
    }
  }
}

customElements.define('header-drawer', HeaderDrawer);

class ModalDialog extends HTMLElement {
  constructor() {
    super();
    const closeButton = this.querySelector('[id^="ModalClose-"]');
    if (closeButton) closeButton.addEventListener('click', this.hide.bind(this, false));
    this.addEventListener('keyup', (event) => {
      if (event.code.toUpperCase() === 'ESCAPE') this.hide();
    });
    if (this.classList.contains('media-modal')) {
      this.addEventListener('pointerup', (event) => {
        if (event.pointerType === 'mouse' && !event.target.closest('deferred-media, product-model')) this.hide();
      });
    } else {
      this.addEventListener('click', (event) => {
        if (event.target === this) this.hide();
      });
    }
  }

  connectedCallback() {
    if (this.moved) return;
    this.moved = true;
    document.body.appendChild(this);
  }

  show(opener) {
    this.openedBy = opener;
    const popup = this.querySelector('.template-popup');
    document.body.classList.add('overflow-hidden');
    this.setAttribute('open', '');
    if (popup) popup.loadContent();
    trapFocus(this, this.querySelector('[role="dialog"]'));
    window.pauseAllMedia();
  }

  hide() {
    document.body.classList.remove('overflow-hidden');
    document.body.dispatchEvent(new CustomEvent('modalClosed'));
    this.removeAttribute('open');
    removeTrapFocus(this.openedBy);
    window.pauseAllMedia();
  }
}
customElements.define('modal-dialog', ModalDialog);

class ModalOpener extends HTMLElement {
  constructor() {
    super();

    const button = this.querySelector('button');

    if (!button) return;
    button.addEventListener('click', () => {
      const modal = document.querySelector(this.getAttribute('data-modal'));
      if (modal && typeof modal.show === 'function') modal.show(button);
    });
  }
}
customElements.define('modal-opener', ModalOpener);

class DeferredMedia extends HTMLElement {
  constructor() {
    super();
    const poster = this.querySelector('[id^="Deferred-Poster-"]');
    if (!poster) return;
    poster.addEventListener('click', this.loadContent.bind(this));
  }

  loadContent(focus = true) {
    window.pauseAllMedia();
    if (!this.getAttribute('loaded')) {
      const content = document.createElement('div');
      content.appendChild(this.querySelector('template').content.firstElementChild.cloneNode(true));

      this.setAttribute('loaded', true);
      const deferredElement = this.appendChild(content.querySelector('video, model-viewer, iframe'));
      if (focus) deferredElement.focus();
      if (deferredElement.nodeName == 'VIDEO' && deferredElement.getAttribute('autoplay')) {
        // force autoplay for safari
        deferredElement.play();
      }
    }
  }
}

customElements.define('deferred-media', DeferredMedia);

window.themeCore = Object.assign(window.themeCore || {}, {
  debounce,
  throttle,
  fetchConfig,
  ModalDialog,
  DeferredMedia,
});
window.debounce = window.debounce || debounce;
window.throttle = window.throttle || throttle;
window.fetchConfig = window.fetchConfig || fetchConfig;
window.ModalDialog = window.ModalDialog || ModalDialog;
window.DeferredMedia = window.DeferredMedia || DeferredMedia;
window.dispatchEvent(new CustomEvent('theme:core-ready'));

class SliderComponent extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.enableSliderLooping = false;
    this.currentPageElement = this.querySelector('.slider-counter--current');
    this.pageTotalElement = this.querySelector('.slider-counter--total');
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');

    this.oriantation = window.matchMedia('(min-width: 768px)').matches ? this.dataset.oriantation ? this.dataset.oriantation : 'horizontal' : 'horizontal';
    if (this.oriantation == 'vertical') {
      this.offsetPosition = 'offsetTop';
      this.scrollPosition = 'scrollTop';
      this.clientPosition = 'clientHeight';
    } else {
      this.offsetPosition = 'offsetLeft';
      this.scrollPosition = 'scrollLeft';
      this.clientPosition = 'clientWidth';
    }

    if (!this.slider || !this.prevButton || !this.nextButton) return;

    this.initPages();
    const resizeObserver = new ResizeObserver((entries) => this.initPages());
    resizeObserver.observe(this.slider);

    this.slider.addEventListener('scroll', this.update.bind(this), { passive: true });
    this.prevButton.addEventListener('click', this.onButtonClick.bind(this));
    this.nextButton.addEventListener('click', this.onButtonClick.bind(this));

    // Enable mouse drag/swipe on desktop
    this.isDragging = false;
    this.startX = 0;
    this.scrollLeft = 0;
    this.initMouseDrag();

    // Initialize dots pagination
    this.initDots();

  }

  initDots() {
    const dotsWrapper = this.querySelector('.slider-counter--dots-wrapper');
    if (!dotsWrapper) return;

    const dots = dotsWrapper.querySelectorAll('.slider-counter__link--dots');
    dots.forEach((dot, index) => {
      dot.addEventListener('click', (e) => {
        e.preventDefault();
        const slideIndex = parseInt(dot.dataset.slideIndex) || index;
        if (this.sliderItemsToShow && this.sliderItemsToShow[slideIndex]) {
          const targetSlide = this.sliderItemsToShow[slideIndex];
          this.setSlidePosition(targetSlide[this.offsetPosition]);
        }
      });
    });
  }


  initMouseDrag() {
    if (!this.slider) return;

    // Mouse events for desktop drag
    this.slider.addEventListener('mousedown', (e) => {
      // Don't start dragging if clicking on interactive elements
      const isButton = e.target.closest('a, button, [role="button"], .thumbnail');
      if (isButton) {
        // Allow the click event to proceed
        return;
      }
      this.isDragging = true;
      this.slider.classList.add('cursor-grabbing');
      this.slider.classList.remove('cursor-grab');
      this.startX = e.pageX - this.slider.offsetLeft;
      this.scrollLeft = this.slider[this.scrollPosition];
    });

    this.slider.addEventListener('mouseleave', () => {
      this.isDragging = false;
      this.slider.classList.add('cursor-grab');
      this.slider.classList.remove('cursor-grabbing');
    });

    this.slider.addEventListener('mouseup', () => {
      this.isDragging = false;
      this.slider.classList.add('cursor-grab');
      this.slider.classList.remove('cursor-grabbing');
    });

    this.slider.addEventListener('mousemove', (e) => {
      if (!this.isDragging) return;
      // Don't prevent default if over a button
      const isButton = e.target.closest('a, button, [role="button"], .thumbnail');
      if (isButton) {
        this.isDragging = false;
        this.isDragging = false;
        this.slider.classList.add('cursor-grab');
        this.slider.classList.remove('cursor-grabbing');
        return;
      }
      e.preventDefault();
      const x = e.pageX - this.slider.offsetLeft;
      const walk = (x - this.startX) * 2; // Scroll speed multiplier
      this.slider[this.scrollPosition] = this.scrollLeft - walk;
    });

    // Touch events for mobile (already works, but ensure it's smooth)
    let touchStartX = 0;
    let touchScrollLeft = 0;
    let touchStartTarget = null;

    this.slider.addEventListener('touchstart', (e) => {
      touchStartTarget = e.target;
      // Don't start dragging if touching a button
      if (touchStartTarget.closest('a, button, [role="button"], .thumbnail')) {
        return;
      }
      touchStartX = e.touches[0].pageX - this.slider.offsetLeft;
      touchScrollLeft = this.slider[this.scrollPosition];
    });

    this.slider.addEventListener('touchmove', (e) => {
      // Don't drag if started on a button
      if (touchStartTarget && touchStartTarget.closest('a, button, [role="button"], .thumbnail')) {
        return;
      }
      if (!this.isDragging) {
        const x = e.touches[0].pageX - this.slider.offsetLeft;
        const walk = (x - touchStartX) * 2;
        this.slider[this.scrollPosition] = touchScrollLeft - walk;
      }
    });

    // Set initial cursor style
    this.slider.classList.add('cursor-grab');
  }

  initPages() {
    this.sliderItemsToShow = Array.from(this.sliderItems).filter((element) => element[this.clientPosition] > 0);
    if (this.sliderItemsToShow.length < 2) return;
    this.sliderItemOffset = this.sliderItemsToShow[1][this.offsetPosition] - this.sliderItemsToShow[0][this.offsetPosition];
    this.slidesPerPage = Math.floor(
      (this.slider.clientHeight - this.sliderItemsToShow[0][this.offsetPosition]) / this.sliderItemOffset
    );
    this.totalPages = this.sliderItemsToShow.length - this.slidesPerPage + 1;
    this.update();
  }

  resetPages() {
    this.sliderItems = this.querySelectorAll('[id^="Slide-"]');
    this.initPages();
  }

  update() {
    // Temporarily prevents unneeded updates resulting from variant changes
    if (!this.slider || !this.prevButton || !this.nextButton) return;

    // Throttle updates using requestAnimationFrame
    if (!this.ticking) {
      window.requestAnimationFrame(() => {
        const previousPage = this.currentPage;
        const sliderPosition = this.slider[this.scrollPosition];
        this.currentPage = Math.round(sliderPosition / this.sliderItemOffset) + 1;

        if (this.currentPageElement && this.pageTotalElement) {
          this.currentPageElement.textContent = this.currentPage;
          this.pageTotalElement.textContent = this.totalPages;
        }

        if (this.currentPage != previousPage) {
          this.dispatchEvent(
            new CustomEvent('slideChanged', {
              detail: {
                currentPage: this.currentPage,
                currentElement: this.sliderItemsToShow[this.currentPage - 1],
              },
            })
          );
        }

        if (this.enableSliderLooping) {
          this.ticking = false;
          return;
        }

        if (this.isSlideVisible(this.sliderItemsToShow[0]) && sliderPosition === 0) {
          this.prevButton.setAttribute('disabled', 'disabled');
        } else {
          this.prevButton.removeAttribute('disabled');
        }

        if (this.isSlideVisible(this.sliderItemsToShow[this.sliderItemsToShow.length - 1])) {
          this.nextButton.setAttribute('disabled', 'disabled');
        } else {
          this.nextButton.removeAttribute('disabled');
        }

        this.ticking = false;
      });

      this.ticking = true;
    }
  }

  isSlideVisible(element, offset = 0) {
    if (!element) return false;
    const lastVisibleSlide = this.slider[this.clientPosition] + this.slider[this.scrollPosition] - offset;
    return element[this.offsetPosition] + element[this.clientPosition] <= lastVisibleSlide && element[this.offsetPosition] >= this.slider[this.scrollPosition];
  }

  onButtonClick(event) {
    event.preventDefault();
    const step = event.currentTarget.dataset.step || 1;
    const sliderPosition = this.slider[this.scrollPosition];
    this.slideScrollPosition =
      event.currentTarget.name === 'next'
        ? sliderPosition + step * this.sliderItemOffset
        : sliderPosition - step * this.sliderItemOffset;
    this.setSlidePosition(this.slideScrollPosition);
  }

  setSlidePosition(position) {
    const param = this.oriantation == 'vertical' ? { top: position } : { left: position };
    this.slider.scrollTo(param);
  }
}

customElements.define('slider-component', SliderComponent);

class SlideshowComponent extends SliderComponent {
  constructor() {
    super();
    this.sliderControlWrapper = this.querySelector('.slider-buttons');
    this.enableSliderLooping = true;

    if (!this.sliderControlWrapper || !this.slider || !this.sliderItemsToShow) return;

    this.sliderFirstItemNode = this.slider.querySelector('.slideshow__slide');
    if (this.sliderItemsToShow.length > 0) this.currentPage = 1;

    this.announcementBarSlider = this.querySelector('.announcement-bar-slider');
    // Value below should match --duration-announcement-bar CSS value
    this.announcerBarAnimationDelay = this.announcementBarSlider ? 250 : 0;

    this.sliderControlLinksArray = Array.from(this.sliderControlWrapper.querySelectorAll('.slider-counter__link'));
    this.sliderControlLinksArray.forEach((link) => link.addEventListener('click', this.linkToSlide.bind(this)));
    this.slider.addEventListener('scroll', this.setSlideVisibility.bind(this), { passive: true });
    this.setSlideVisibility();

    if (this.announcementBarSlider) {
      this.announcementBarArrowButtonWasClicked = false;

      this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.reducedMotion.addEventListener('change', () => {
        if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
      });

      [this.prevButton, this.nextButton].forEach((button) => {
        button.addEventListener(
          'click',
          () => {
            this.announcementBarArrowButtonWasClicked = true;
          },
          { once: true }
        );
      });
    }

    if (this.slider.getAttribute('data-autoplay') === 'true') this.setAutoPlay();
  }

  setAutoPlay() {
    this.autoplaySpeed = this.slider.dataset.speed * 1000;
    this.addEventListener('mouseover', this.focusInHandling.bind(this));
    this.addEventListener('mouseleave', this.focusOutHandling.bind(this));
    this.addEventListener('focusin', this.focusInHandling.bind(this));
    this.addEventListener('focusout', this.focusOutHandling.bind(this));

    if (this.querySelector('.slideshow__autoplay')) {
      this.sliderAutoplayButton = this.querySelector('.slideshow__autoplay');
      this.sliderAutoplayButton.addEventListener('click', this.autoPlayToggle.bind(this));
      this.autoplayButtonIsSetToPlay = true;
      this.play();
    } else {
      this.reducedMotion.matches || this.announcementBarArrowButtonWasClicked ? this.pause() : this.play();
    }
  }

  onButtonClick(event) {
    super.onButtonClick(event);
    this.wasClicked = true;

    const isFirstSlide = this.currentPage === 1;
    const isLastSlide = this.currentPage === this.sliderItemsToShow.length;

    if (!isFirstSlide && !isLastSlide) {
      this.applyAnimationToAnnouncementBar(event.currentTarget.name);
      return;
    }

    if (isFirstSlide && event.currentTarget.name === 'previous') {
      this.slideScrollPosition =
        this.slider.scrollLeft + this.sliderFirstItemNode.clientWidth * this.sliderItemsToShow.length;
    } else if (isLastSlide && event.currentTarget.name === 'next') {
      this.slideScrollPosition = 0;
    }

    this.setSlidePosition(this.slideScrollPosition);

    this.applyAnimationToAnnouncementBar(event.currentTarget.name);
  }

  setSlidePosition(position) {
    if (this.setPositionTimeout) clearTimeout(this.setPositionTimeout);
    this.setPositionTimeout = setTimeout(() => {
      this.slider.scrollTo({
        left: position,
      });
    }, this.announcerBarAnimationDelay);
  }

  update() {
    super.update();
    this.sliderControlButtons = this.querySelectorAll('.slider-counter__link');
    this.prevButton.removeAttribute('disabled');

    if (!this.sliderControlButtons.length) return;

    this.sliderControlButtons.forEach((link) => {
      link.classList.remove('slider-counter__link--active');
      link.removeAttribute('aria-current');
    });
    this.sliderControlButtons[this.currentPage - 1].classList.add('slider-counter__link--active');
    this.sliderControlButtons[this.currentPage - 1].setAttribute('aria-current', true);
  }

  autoPlayToggle() {
    this.togglePlayButtonState(this.autoplayButtonIsSetToPlay);
    this.autoplayButtonIsSetToPlay ? this.pause() : this.play();
    this.autoplayButtonIsSetToPlay = !this.autoplayButtonIsSetToPlay;
  }

  focusOutHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (!this.autoplayButtonIsSetToPlay || focusedOnAutoplayButton) return;
      this.play();
    } else if (!this.reducedMotion.matches && !this.announcementBarArrowButtonWasClicked) {
      this.play();
    }
  }

  focusInHandling(event) {
    if (this.sliderAutoplayButton) {
      const focusedOnAutoplayButton =
        event.target === this.sliderAutoplayButton || this.sliderAutoplayButton.contains(event.target);
      if (focusedOnAutoplayButton && this.autoplayButtonIsSetToPlay) {
        this.play();
      } else if (this.autoplayButtonIsSetToPlay) {
        this.pause();
      }
    } else if (this.announcementBarSlider.contains(event.target)) {
      this.pause();
    }
  }

  play() {
    this.slider.setAttribute('aria-live', 'off');
    clearInterval(this.autoplay);
    this.autoplay = setInterval(this.autoRotateSlides.bind(this), this.autoplaySpeed);
  }

  pause() {
    this.slider.setAttribute('aria-live', 'polite');
    clearInterval(this.autoplay);
  }

  togglePlayButtonState(pauseAutoplay) {
    if (pauseAutoplay) {
      this.sliderAutoplayButton.classList.add('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.playSlideshow);
    } else {
      this.sliderAutoplayButton.classList.remove('slideshow__autoplay--paused');
      this.sliderAutoplayButton.setAttribute('aria-label', window.accessibilityStrings.pauseSlideshow);
    }
  }

  autoRotateSlides() {
    const slideScrollPosition =
      this.currentPage === this.sliderItems.length ? 0 : this.slider.scrollLeft + this.sliderItemOffset;

    this.setSlidePosition(slideScrollPosition);
    this.applyAnimationToAnnouncementBar();
  }

  setSlideVisibility(event) {
    if (!this.visibilityTicking) {
      window.requestAnimationFrame(() => {
        this.sliderItemsToShow.forEach((item, index) => {
          const linkElements = item.querySelectorAll('a');
          if (index === this.currentPage - 1) {
            if (linkElements.length)
              linkElements.forEach((button) => {
                button.removeAttribute('tabindex');
              });
            item.setAttribute('aria-hidden', 'false');
            item.removeAttribute('tabindex');
          } else {
            if (linkElements.length)
              linkElements.forEach((button) => {
                button.setAttribute('tabindex', '-1');
              });
            item.setAttribute('aria-hidden', 'true');
            item.setAttribute('tabindex', '-1');
          }
        });
        this.wasClicked = false;
        this.visibilityTicking = false;
      });
      this.visibilityTicking = true;
    }
  }

  applyAnimationToAnnouncementBar(button = 'next') {
    if (!this.announcementBarSlider) return;

    const itemsCount = this.sliderItems.length;
    const increment = button === 'next' ? 1 : -1;

    const currentIndex = this.currentPage - 1;
    let nextIndex = (currentIndex + increment) % itemsCount;
    nextIndex = nextIndex === -1 ? itemsCount - 1 : nextIndex;

    const nextSlide = this.sliderItems[nextIndex];
    const currentSlide = this.sliderItems[currentIndex];

    const animationClassIn = 'announcement-bar-slider--fade-in';
    const animationClassOut = 'announcement-bar-slider--fade-out';

    const isFirstSlide = currentIndex === 0;
    const isLastSlide = currentIndex === itemsCount - 1;

    const shouldMoveNext = (button === 'next' && !isLastSlide) || (button === 'previous' && isFirstSlide);
    const direction = shouldMoveNext ? 'next' : 'previous';

    currentSlide.classList.add(`${animationClassOut}-${direction}`);
    nextSlide.classList.add(`${animationClassIn}-${direction}`);

    setTimeout(() => {
      currentSlide.classList.remove(`${animationClassOut}-${direction}`);
      nextSlide.classList.remove(`${animationClassIn}-${direction}`);
    }, this.announcerBarAnimationDelay * 2);
  }

  linkToSlide(event) {
    event.preventDefault();
    const slideScrollPosition =
      this.slider.scrollLeft +
      this.sliderFirstItemNode.clientWidth *
      (this.sliderControlLinksArray.indexOf(event.currentTarget) + 1 - this.currentPage);
    this.slider.scrollTo({
      left: slideScrollPosition,
    });
  }
}

customElements.define('slideshow-component', SlideshowComponent);


// Variants
class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // Use event delegation to catch change events from inputs and selects
    this.addEventListener('change', (event) => {

      // Ensure we're handling change events from radio inputs or selects
      const isRadioInput = event.target.type === 'radio';
      const isSelect = event.target.tagName === 'SELECT';

      if (isRadioInput || isSelect) {
        const target = this.getInputForEventTarget(event.target);
        this.updateSelectionMetadata(event);
        this.setPendingOption(target);

        // Get selected option values - ensure we get them after the input is checked
        const selectedValues = this.selectedOptionValues;

        publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
          data: {
            event,
            target,
            selectedOptionValues: selectedValues,
          },
        });

        // Fallback: Always try to directly call product-info handler as backup
        // This ensures the handler runs even if subscription failed or element was replaced
        // Use setTimeout to let the pub/sub system try first, then fallback if needed
        setTimeout(() => {
          if (event.productInfoHandled) return;

          // Find product-info by matching section/product ID instead of just DOM containment
          const variantSelector = this; // 'this' is the variant-selects/color-swatch element
          const variantSectionId = variantSelector.dataset.section;
          const variantProductId = variantSelector.dataset.product;

          // Try to find matching product-info by section or product ID
          let productInfo = this.closest('product-info');
          if (!productInfo || (variantSectionId && productInfo.dataset.section !== variantSectionId && productInfo.dataset.originalSection !== variantSectionId)) {
            // Find by section ID match
            productInfo = document.querySelector(`product-info[data-section="${variantSectionId}"], product-info[data-original-section="${variantSectionId}"]`);
          }
          if (!productInfo && variantProductId) {
            // Fallback: find by product ID match
            productInfo = document.querySelector(`product-info[data-product-id="${variantProductId}"]`);
          }
          if (!productInfo) {
            // Last resort: main product page
            productInfo = document.querySelector('product-info[id^="MainProduct"]');
          }

          if (productInfo && typeof productInfo.handleOptionValueChange === 'function') {
            productInfo.handleOptionValueChange({
              data: {
                event,
                target,
                selectedOptionValues: selectedValues,
              },
            });
          }
        }, 10);
      }
    });

    // Ensure label clicks properly trigger radio inputs
    // This is a fallback in case the native label behavior doesn't work
    this.addEventListener('click', (event) => {
      const label = event.target.closest('label[for]');
      if (label && this.contains(label)) {
        const inputId = label.getAttribute('for');
        if (inputId) {
          // Clean up the ID (remove any leading #)
          const cleanId = inputId.replace(/^#/, '');
          const input = document.getElementById(cleanId) || this.querySelector(`#${CSS.escape(cleanId)}`);

          if (input && input.type === 'radio' && !input.disabled && input.closest('variant-selects, color-swatch') === this) {
            // Check if input is already checked (browser should have done this)
            // If not, manually check it and trigger change event
            if (!input.checked) {
              input.checked = true;
              // Trigger change event after a microtask to ensure it fires
              Promise.resolve().then(() => {
                input.dispatchEvent(new Event('change', { bubbles: true, cancelable: true }));
              });
            }
          }
        }
      }
    }, { passive: true });
  }


  updateSelectionMetadata({ target }) {
    const { value, tagName } = target;

    if (tagName === 'SELECT' && target.selectedOptions.length) {
      Array.from(target.options)
        .find((option) => option.getAttribute('selected'))
        .removeAttribute('selected');
      target.selectedOptions[0].setAttribute('selected', 'selected');

      const swatchValue = target.selectedOptions[0].dataset.optionSwatchValue;
      const selectedDropdownSwatchValue = target
        .closest('.product-form__input')
        .querySelector('[data-selected-value] > .swatch');
      if (!selectedDropdownSwatchValue) return;
      if (swatchValue) {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', swatchValue);
        selectedDropdownSwatchValue.classList.remove('swatch--unavailable');
      } else {
        selectedDropdownSwatchValue.style.setProperty('--swatch--background', 'unset');
        selectedDropdownSwatchValue.classList.add('swatch--unavailable');
      }

      selectedDropdownSwatchValue.style.setProperty(
        '--swatch-focal-point',
        target.selectedOptions[0].dataset.optionSwatchFocalPoint || 'unset'
      );
    } else if (tagName === 'INPUT' && target.type === 'radio') {
      const fieldset = target.closest(`fieldset`);
      if (!fieldset) return;

      // Update data-selected-value (for dropdown swatch display)
      const selectedSwatchValue = fieldset.querySelector('[data-selected-value]');
      if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;

      // Update data-selected-swatch-value (used in swatch picker type - this is the main one!)
      const selectedSwatchValueSpan = fieldset.querySelector('[data-selected-swatch-value]');
      if (selectedSwatchValueSpan) {
        selectedSwatchValueSpan.textContent = value;
      }

      // Update the legend span if it exists (for button/pill picker type)
      const legend = fieldset.querySelector('legend');
      if (legend) {
        const legendSpan = legend.querySelector('span');
        if (legendSpan && !legendSpan.hasAttribute('data-selected-swatch-value') && !legendSpan.hasAttribute('data-selected-value')) {
          legendSpan.textContent = value;
        }
      }
    }
  }

  getInputForEventTarget(target) {
    return target.tagName === 'SELECT' ? target.selectedOptions[0] : target;
  }

  setPendingOption(target) {
    this.querySelectorAll('label.is-loading').forEach((label) => label.classList.remove('is-loading'));
    if (!target?.id) return;

    const label = this.querySelector(`label[for="${CSS.escape(target.id)}"]`);
    label?.classList.add('is-loading');
  }

  get selectedOptionValues() {
    const selectedInputs = Array.from(
      this.querySelectorAll('select option[selected], fieldset input[type="radio"]:checked, input[type="radio"]:checked')
    );

    const result = selectedInputs
      .map((element) => {
        // Try dataset first (for data-option-value-id)
        if (element.dataset && element.dataset.optionValueId) {
          return element.dataset.optionValueId;
        }
        // Fallback: try getAttribute directly
        const attrValue = element.getAttribute('data-option-value-id');
        if (attrValue) {
          return attrValue;
        }
        return null;
      })
      .filter((value) => value !== null);

    return result;
  }
}

customElements.define('variant-selects', VariantSelects);


class ProductRecommendations extends HTMLElement {

  constructor() {
    super();
    this.observer = undefined;
  }

  connectedCallback() {
    this.initializeRecommendations(this.dataset.productId);
  }

  initializeRecommendations(productId) {
    this.observer?.unobserve(this);
    this.observer = new IntersectionObserver(
      (entries, observer) => {
        if (!entries[0].isIntersecting) return;
        observer.unobserve(this);
        this.loadRecommendations(productId);
      },
      { rootMargin: '0px 0px 400px 0px' }
    );
    this.observer.observe(this);
  }

  loadRecommendations(productId) {
    fetch(`${this.dataset.url}&product_id=${productId}&section_id=${this.dataset.sectionId}`)
      .then((response) => response.text())
      .then((text) => {
        const html = document.createElement('div');
        html.innerHTML = text;
        const recommendations = html.querySelector('product-recommendations');

        if (recommendations?.innerHTML.trim().length) {
          this.innerHTML = recommendations.innerHTML;
        }

        if (!this.querySelector('slider-component') && this.classList.contains('complementary-products')) {
          this.remove();
        }

        if (html.querySelector('.grid__item')) {
          this.classList.add('product-recommendations--loaded');
        }
      })
      .catch((e) => {
        console.error(e);
      });
  }
}

customElements.define('product-recommendations', ProductRecommendations);



class AccountIcon extends HTMLElement {
  constructor() {
    super();

    this.icon = this.querySelector('.icon');
  }

  connectedCallback() {
    document.addEventListener('storefront:signincompleted', this.handleStorefrontSignInCompleted.bind(this));
  }

  handleStorefrontSignInCompleted(event) {
    if (event?.detail?.avatar) {
      this.icon?.replaceWith(event.detail.avatar.cloneNode());
    }
  }
}

customElements.define('account-icon', AccountIcon);

// Variant hover
class ColorSwatch extends VariantSelects {
  constructor() {
    super();
    this.querySelectorAll('fieldset label').forEach((ele) => {
      ele.addEventListener('click', this.onLabelClick.bind(this));
    })
  }
  onLabelClick(event) {
    const label = event.target.closest('label');
    if (!label) return;

    const inputId = label.getAttribute('for');
    const input = inputId ? this.querySelector(`#${CSS.escape(inputId)}`) : null;
    if (input && input.type === 'radio') {
      return;
    }

    if (label.dataset.href) {
      window.location.href = label.dataset.href;
    }
  }
}
customElements.define('color-swatch', ColorSwatch);


// login popup
class ContentDropdown extends HTMLElement {
  constructor() {
    super();
    this.addEventListener('click', this.popUpClick.bind(this));
    document.addEventListener('click', this.closePopup.bind(this));
  }
  popUpClick(event) {
    event.stopPropagation();
    this.querySelector("#content_dropdown").classList.toggle("hidden");
  }
  closePopup(event) {
    const userPopup = this.querySelector("#content_dropdown");
    if (!userPopup.contains(event.target)) {
      userPopup.classList.add("hidden");
    }
  }
}
customElements.define('content-dropdown', ContentDropdown);

// Collapsible content
class CollapseContent extends HTMLElement {
  constructor() {
    super();
    var collapse = this.getElementsByClassName("toggle");
    Array.from(collapse).forEach((ele) => {
      ele.setAttribute('tabindex', '0');
      // Find the collapse element more reliably
      var content_data = ele.nextElementSibling;
      // If nextElementSibling doesn't have the collapse class, search for it
      if (!content_data || !content_data.classList.contains('collapse')) {
        var parent = ele.parentElement;
        if (parent) {
          content_data = parent.querySelector('.collapse');
        }
      }
      var defaultOpen = ele.classList.contains("active");
      if (content_data && !defaultOpen) {
        content_data.classList.add('is-collapsed');
        content_data.setAttribute('data-collapsed', 'true');
      } else if (content_data && defaultOpen) {
        content_data.classList.remove('is-collapsed');
        content_data.setAttribute('data-collapsed', 'false');
      }
      ele.addEventListener("click", this.onCollapseClick.bind(this));
      ele.addEventListener('keydown', this.handleKeyDown.bind(this));
    });
  }
  handleKeyDown(event) {
    if (event.keyCode === 13) {
      this.onCollapseClick(event);
    }
  }
  onCollapseClick(event) {
    const toggle = event.currentTarget;
    toggle.classList.toggle("active");

    // Find the collapse element more reliably
    let content_data = toggle.nextElementSibling;
    if (!content_data || !content_data.classList.contains('collapse')) {
      const parent = toggle.parentElement;
      if (parent) {
        content_data = parent.querySelector('.collapse');
      }
    }

    if (!content_data) {
      console.warn('Collapse content element not found');
      return;
    }

    const isCollapsed = content_data.getAttribute('data-collapsed') === 'true';
    if (isCollapsed) {
      this.expandCollapse(content_data);
      toggle.setAttribute('aria-expanded', 'true');
    } else {
      this.allCollapse(content_data);
      toggle.setAttribute('aria-expanded', 'false');
    }
  }

  expandCollapse(element) {
    const collapseHeight = element.scrollHeight;
    element.classList.remove('is-collapsed');
    element.style.height = collapseHeight + 'px';
    element.setAttribute('data-collapsed', 'false');

    const handleTransitionEnd = (event) => {
      if (event.propertyName === 'height') {
        element.removeEventListener('transitionend', handleTransitionEnd);
        element.style.height = 'auto';
      }
    };
    element.addEventListener('transitionend', handleTransitionEnd);

    // Fallback if no transition
    setTimeout(() => {
      if (element.style.height === collapseHeight + 'px') {
        element.style.height = 'auto';
        element.removeEventListener('transitionend', handleTransitionEnd);
      }
    }, 500);
  }

  allCollapse(element) {
    const collapseHeight = element.scrollHeight;
    element.style.height = collapseHeight + 'px';
    element.offsetHeight; // force reflow

    element.style.height = '0px';
    element.setAttribute('data-collapsed', 'true');

    const handleTransitionEnd = (event) => {
      if (event.propertyName === 'height') {
        element.removeEventListener('transitionend', handleTransitionEnd);
        element.classList.add('is-collapsed');
        element.style.height = null;
      }
    };
    element.addEventListener('transitionend', handleTransitionEnd);

    // Fallback if no transition
    setTimeout(() => {
      if (element.style.height === '0px') {
        element.classList.add('is-collapsed');
        element.style.height = null;
        element.removeEventListener('transitionend', handleTransitionEnd);
      }
    }, 500);
  }
}
customElements.define('collapse-content', CollapseContent);



// Carousel
class SliderCarousel extends HTMLElement {
  constructor() {
    super();
    let optionsData = {};
    let defaults = {};
    let autoplay = false;
    if (this.dataset.sectionSetting) {
      const sectionSetting = JSON.parse(this.dataset.sectionSetting);
      const space = +this.dataset.spacingGridHorizontal;
      const spacemobile = +this.dataset.spacingGridHorizontal / 2;
      optionsData = {
        slidesPerView: sectionSetting.columns_desktop ? sectionSetting.columns_desktop : 1,
        spaceBetween: space,
        loop: false,
        dragClickables: false,
        updateOnWindowResize: true,
        pagination: {
          clickable: 'true',
        },
        breakpoints: {
          0: {
            slidesPerView: sectionSetting.columns_mobile,
            spaceBetween: spacemobile,
          },
          768: {
            slidesPerView: sectionSetting.columns_desktop,
            spaceBetween: space,
          }
        }
      };
      if (sectionSetting.content_type == true) {
        optionsData['breakpoints'] = {
          0: {
            spaceBetween: spacemobile,
            centeredSlides: true,
            slidesPerView: 1.1,
          },
          768: {
            slidesPerView: 1.2,
            centeredSlides: true,
            spaceBetween: space,
          },
          992: {
            slidesPerView: 1.9,
            centeredSlides: true,
            spaceBetween: space,
          }
        };
      }
      if (sectionSetting.content_type == false) {
        optionsData['breakpoints'] = {
          0: {
            spaceBetween: spacemobile,
            centeredSlides: false,
            slidesPerView: 1,
          },
          768: {
            slidesPerView: 1,
            centeredSlides: false,
            spaceBetween: space,
          }
        };
      }
      if (sectionSetting.enable_loop == true) {
        optionsData['loop'] = {
          loop: true,
          rewind: true,
        };
      }
      if (sectionSetting.transition == 'tran_slide') {
        optionsData['effect'] = 'slide';
      }
      if (sectionSetting.transition == 'tran_card') {
        optionsData['effect'] = 'cards';
      }
      if (sectionSetting.transition == 'tran_coverflow') {
        optionsData['effect'] = 'coverflow';
      }

      if (this.dataset.breakpoints) {
        const breakpoints = JSON.parse(this.dataset.breakpoints)
        for (const key in breakpoints) {
          if (key == 0) {
            let obj = {
              slidesPerView: breakpoints[key],
              spaceBetween: spacemobile,
            }
            optionsData['breakpoints'][key] = obj
          } else {
            let customspaceBetween = space
            let breakpointsKey = key
            if (key.indexOf('m') > -1) {
              breakpointsKey = key.split('-')[1]
              customspaceBetween = spacemobile
            }
            let obj = {
              slidesPerView: breakpoints[key],
              spaceBetween: customspaceBetween,
            }
            optionsData['breakpoints'][breakpointsKey] = obj
          }
        }
      }
      if (sectionSetting.auto_rotate == true) {
        autoplay = true;
        optionsData['autoplay'] = {
          delay: sectionSetting.change_slides_speed * 1000,
          disableOnInteraction: false,
        };
      }
      if (sectionSetting.pagination_style == 'arrows' || sectionSetting.enable_slide_arrow == true) {
        optionsData['navigation'] = {
          nextEl: '.swiper-button-next',
          prevEl: '.swiper-button-prev',
        };
      }

      if (sectionSetting.pagination_style == 'dots' || sectionSetting.pagination_style == 'line' || sectionSetting.pagination_style == 'counter' || sectionSetting.pagination_style == 'numbers') {
        optionsData['pagination']['el'] = '.swiper-pagination';
      }
      if (sectionSetting.pagination_style == 'line') {
        optionsData['pagination']['type'] = 'bullets';
        optionsData['pagination']['renderBullet'] = function (index, className) {
          return '<span class="' + className + '">' + '<i></i>' + '<b></b>' + '</span>';
        };
      }
      if (sectionSetting.pagination_style == 'counter') {
        optionsData['pagination']['type'] = 'fraction';
        optionsData['pagination']['renderBullet'] = function (index, className) {
          return '<span class="' + className + '">' + '<i></i>' + '<b></b>' + '</span>';
        };
      }
      if (sectionSetting.pagination_style == 'numbers') {
        optionsData['pagination']['renderBullet'] = function (index, className) {
          return `<span class="${className}">${index + 1}</span>`
        };
      }
    }
    this.options = {
      ...defaults,
      ...optionsData
    };
    this.swiperInstance = null;
  }
  connectedCallback() {
    // Wait for Swiper library to be available, then initialize
    this.initSwiper();
  }
  initSwiper() {
    const swiperEl = this.querySelector('.swiper');
    if (!swiperEl) return;
    if (swiperEl.swiper) return;

    if (typeof Swiper === 'undefined') {
      if (window.ensureSwiperLoaded) {
        window.ensureSwiperLoaded().then(() => this.initSwiper()).catch(() => {});
      } else {
        window.addEventListener('swiperLoaded', () => this.initSwiper(), { once: true });
      }
      return;
    }

    this.swiperInstance = new Swiper(swiperEl, this.options);
  }
}
customElements.define('slider-carousel', SliderCarousel);


// Image comparison
class ImgCompare extends HTMLElement {
  constructor() {
    super();
    this.topImage = this.querySelectorAll('.compare_image')[1];
    this.inputCompare = this.querySelector('.compare_line');
    const compare_cursor = this.querySelector('#compare_cursor');
    compare_cursor.addEventListener('mousemove', this.resizeImg.bind(this));
    compare_cursor.addEventListener('touchmove', this.resizeImg.bind(this));
  }
  resizeImg(event) {
    this.topImage.style.width = `${event.target.value}%`;
    this.inputCompare.style.left = `${event.target.value}%`;
  }
  connectedCallback() {
    this.querySelectorAll('.comparewidth').forEach(ele => {
      ele.style.width = this.clientWidth + 'px';
    });
  }
}
customElements.define('img-compare', ImgCompare);


// Video - CRITICAL FIX: Defer DOM query to reduce TBT
(function initVideoContentRemove() {
  function setupVideoContentRemove() {
    document.querySelectorAll('.video-content-remove').forEach((close) => {
      close.addEventListener('click', (event) => {
        const parentElement = event.currentTarget.closest('.video_box');
        if (parentElement) {
          parentElement.remove();
        }
      });
    });
  }

  // Defer initialization to reduce TBT
  if (document.readyState === 'loading') {
    if ('requestIdleCallback' in window) {
      document.addEventListener('DOMContentLoaded', function () {
        requestIdleCallback(setupVideoContentRemove, { timeout: 2000 });
      });
    } else {
      document.addEventListener('DOMContentLoaded', function () {
        setTimeout(setupVideoContentRemove, 2000);
      });
    }
  } else {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(setupVideoContentRemove, { timeout: 2000 });
    } else {
      setTimeout(setupVideoContentRemove, 2000);
    }
  }
})();


// Menu hover
class DesktopWebiMenu extends HTMLElement {
  constructor() {
    super();
    if (window.outerWidth > 991) {
      const drop = this.dataset.drop ? this.dataset.drop : 'click';
      this.addEventListener('click', this.onFocusOut.bind(this));
      this.addEventListener('keydown', this.handleKeyDown.bind(this));
      this.querySelectorAll('li.menu_space').forEach(ele => {
        ele.addEventListener('keydown', this.handleLiKeyDown.bind(this));
        ele.addEventListener(drop, () => {
          this.onLiClick(ele);
        });
        if (drop == 'mouseover') {
          ele.addEventListener('mouseout', () => {
            this.closeLi();
          });
        }
      });
    }
  }
  removeSubMenuClass(ele) {
    ele.querySelectorAll('.submenu_child').forEach((e) => {
      e.classList.remove('submenu_keyboard')
    });
  }
  addSubMenuClass(ele) {
    ele.querySelectorAll('.submenu_child').forEach((e) => {
      e.classList.add('submenu_keyboard')
    });
  }
  handleLiKeyDown(event) {
    if (event.keyCode === 13) {
      if (event.target.classList.contains('menuclick')) {
        event.target.classList.remove('menuclick');
        this.removeSubMenuClass(event.target);
      }
      else
        this.onLiClick(event.target);
      if (event.target.classList.contains('onlychild_focus')) {
        event.preventDefault();
      }
    }
  }
  handleKeyDown(event) {
    if (event.keyCode === 13) {
      this.onFocusOut(event);
      if (event.target.classList.contains('onlychild_focus')) {
        event.preventDefault();
      }
    }
  }
  onLiClick(ele) {
    if (ele.nodeName != 'LI') return;
    this.closeLi();
    ele.classList.add('menuclick');
    this.addSubMenuClass(ele);

    // Position dropdown menu relative to clicked element for sticky headers
    const dropdown = ele.querySelector('.menudrop_hover');
    if (dropdown) {
      // Check if this is a click dropdown menu that needs positioning
      const clickDropMenu = ele.querySelector('.click_dropmenu');
      if (clickDropMenu) {
        this.positionDropdown(ele, dropdown);
      }
    }
  }

  positionDropdown(menuItem, dropdown) {
    // Add class to indicate this dropdown is positioned via JS
    dropdown.classList.add('js-positioned');

    // Get the menu item's position relative to viewport
    const rect = menuItem.getBoundingClientRect();

    // Set the dropdown position - use fixed positioning to account for sticky header
    dropdown.style.setProperty('position', 'fixed', 'important');
    dropdown.style.setProperty('top', `${rect.bottom}px`, 'important');
    dropdown.style.setProperty('left', `${rect.left}px`, 'important');
    dropdown.style.setProperty('right', 'auto', 'important');

    // Function to update dropdown position
    const updatePosition = () => {
      if (!menuItem.classList.contains('menuclick')) {
        return;
      }

      const newRect = menuItem.getBoundingClientRect();
      const dropdownWidth = dropdown.offsetWidth || 250;
      const viewportWidth = window.innerWidth;

      dropdown.style.setProperty('top', `${newRect.bottom}px`, 'important');

      // Ensure dropdown doesn't go off-screen horizontally
      if (newRect.left + dropdownWidth > viewportWidth) {
        dropdown.style.setProperty('left', `${Math.max(10, viewportWidth - dropdownWidth - 10)}px`, 'important');
      } else {
        dropdown.style.setProperty('left', `${newRect.left}px`, 'important');
      }
    };

    // Initial position calculation
    updatePosition();

    // Store reference for cleanup
    if (!this.activeDropdowns) {
      this.activeDropdowns = new Map();
    }

    // Remove old listeners if exists for this dropdown
    if (this.activeDropdowns.has(dropdown)) {
      const oldListeners = this.activeDropdowns.get(dropdown);
      window.removeEventListener('scroll', oldListeners.scroll);
      window.removeEventListener('resize', oldListeners.resize);
    }

    // Cleanup function
    const cleanup = () => {
      if (this.activeDropdowns && this.activeDropdowns.has(dropdown)) {
        const listeners = this.activeDropdowns.get(dropdown);
        window.removeEventListener('scroll', listeners.scroll);
        window.removeEventListener('resize', listeners.resize);
        this.activeDropdowns.delete(dropdown);
      }
    };

    // Create scroll handler
    const handleScroll = () => {
      if (!menuItem.classList.contains('menuclick')) {
        cleanup();
        return;
      }
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          updatePosition();
          this.ticking = false;
        });
        this.ticking = true;
      }
    };

    // Create resize handler
    const handleResize = () => {
      if (!menuItem.classList.contains('menuclick')) {
        cleanup();
        return;
      }
      if (!this.ticking) {
        window.requestAnimationFrame(() => {
          updatePosition();
          this.ticking = false;
        });
        this.ticking = true;
      }
    };

    // Store and add listeners
    this.activeDropdowns.set(dropdown, { scroll: handleScroll, resize: handleResize });
    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize, { passive: true });
  }
  closeLi() {
    this.querySelectorAll('li.menuclick').forEach((ele) => {
      ele.classList.remove('menuclick');
      this.removeSubMenuClass(ele);

      // Clean up scroll and resize listeners for closed dropdowns
      const dropdown = ele.querySelector('.menudrop_hover');
      if (dropdown && this.activeDropdowns && this.activeDropdowns.has(dropdown)) {
        const listeners = this.activeDropdowns.get(dropdown);
        window.removeEventListener('scroll', listeners.scroll);
        window.removeEventListener('resize', listeners.resize);
        this.activeDropdowns.delete(dropdown);
        // Reset dropdown positioning
        dropdown.classList.remove('js-positioned');
        dropdown.style.removeProperty('position');
        dropdown.style.removeProperty('top');
        dropdown.style.removeProperty('left');
        dropdown.style.removeProperty('right');
      }
    })
  }
  onFocusOut(event) {
    event.stopPropagation();
    if (this.contains(event.target) && (!this.querySelector('.wbmenu_js').contains(event.target))) {
      if (this.classList.contains('open')) {
        this.classList.remove('open');
        this.closeLi();
      }
      else this.classList.add('open');
    } else if (!this.querySelector('.wbmenu_js').contains(event.target)) {
      this.classList.remove('open');
      this.closeLi();
    }
    if ("ontouchstart" in document.documentElement && event.target.closest('.header__menu-item').classList.contains('summary'))
      event.preventDefault();
  }
}
customElements.define('desktop-webi-menu', DesktopWebiMenu);

class DrawerIcon extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('.drawericon_inner').forEach(ele => {
      ele.addEventListener('click', this.onIconClick.bind(this))
    });
    this.contentElements = this.querySelectorAll('.drawer_tab');
    this.prevTag = '';
  }

  onIconClick(event) {
    let mainDiv = event.target.closest('button');
    this.contentElements.forEach(ele => {
      if (ele.dataset.contant == mainDiv.dataset.label) {
        if (this.prevTag == ele.dataset.contant) {
          this.querySelector('.drawericon').classList.remove('active');
          this.prevTag = '';
        }
        else {
          this.querySelector('.drawericon').classList.add('active');
          this.prevTag = ele.dataset.contant;
        }
        ele.classList.toggle('active');
        mainDiv.classList.toggle('active');
      } else {
        ele.classList.remove('active');
        this.querySelector('button[data-label="' + ele.dataset.contant + '"]').classList.remove('active');
      }
    });
  }

}
customElements.define('drawer-icon', DrawerIcon);


// Bulk modal
class BulkModal extends HTMLElement {
  constructor() {
    super();
  }
  connectedCallback() {
    const handleIntersection = (entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.unobserve(this);
      if (this.innerHTML.trim() === '') {
        const productUrl = this.dataset.url.split('?')[0];
        fetch(`${productUrl}?section_id=bulk-quick-order-list`)
          .then((response) => response.text())
          .then((responseText) => {
            const html = new DOMParser().parseFromString(responseText, 'text/html');
            const sourceQty = html.querySelector('.quick-order-list-container').parentNode;
            this.innerHTML = sourceQty.innerHTML;
          })
          .catch((e) => {
            console.error(e);
          });
      }
    };
    new IntersectionObserver(handleIntersection.bind(this)).observe(
      document.querySelector(`#QuickBulk-${this.dataset.productId}-${this.dataset.sectionId}`)
    );
  }
}

customElements.define('bulk-modal', BulkModal);



// Bulk add
class BulkAdd extends HTMLElement {
  constructor() {
    super();
    this.queue = [];
    this.requestStarted = false;
    this.ids = [];
  }

  startQueue(id, quantity) {
    this.queue.push({ id, quantity });
    const interval = setInterval(() => {
      if (this.queue.length > 0) {
        if (!this.requestStarted) {
          this.sendRequest(this.queue);
        }
      } else {
        clearInterval(interval);
      }
    }, 250);
  }

  sendRequest(queue) {
    this.requestStarted = true;
    const items = {};
    queue.forEach((queueItem) => {
      items[parseInt(queueItem.id)] = queueItem.quantity;
    });
    this.queue = this.queue.filter((queueElement) => !queue.includes(queueElement));
    const quickBulkElement = this.closest('quick-order-list') || this.closest('quick-add-bulk');
    quickBulkElement.updateMultipleQty(items);
  }

  resetQuantityInput(id) {
    const input = this.querySelector(`#Quantity-${id}`);
    input.value = input.getAttribute('value');
    this.isEnterPressed = false;
  }

  setValidity(event, index, message) {
    event.target.setCustomValidity(message);
    event.target.reportValidity();
    this.resetQuantityInput(index);
    event.target.select();
  }

  validateQuantity(event) {
    const inputValue = parseInt(event.target.value);
    const index = event.target.dataset.index;

    if (inputValue < event.target.dataset.min) {
      this.setValidity(event, index, window.quickOrderListStrings.min_error.replace('[min]', event.target.dataset.min));
    } else if (inputValue > parseInt(event.target.max)) {
      this.setValidity(event, index, window.quickOrderListStrings.max_error.replace('[max]', event.target.max));
    } else if (inputValue % parseInt(event.target.step) != 0) {
      this.setValidity(event, index, window.quickOrderListStrings.step_error.replace('[step]', event.target.step));
    } else {
      event.target.setCustomValidity('');
      event.target.reportValidity();
      this.startQueue(index, inputValue);
    }
  }

  getSectionsUrl() {
    if (window.location.pathname == '/search') {
      const urlParams = new URLSearchParams(window.location.search);
      const queryString = urlParams.get('q');
      return `${window.location.pathname}?q=${queryString}`;
    } else {
      if (window.pageNumber) {
        return `${window.location.pathname}?page=${window.pageNumber}`;
      } else {
        return `${window.location.pathname}`;
      }
    }
  }

  getSectionInnerHTML(html, selector) {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }
}

if (!customElements.get('bulk-add')) {
  customElements.define('bulk-add', BulkAdd);
}


// Product card image gallery
(function () {
  if (window.cardImageGalleryInitialized) return;
  window.cardImageGalleryInitialized = true;

  function getGalleryImages(card) {
    if (card.cardGalleryImages) return card.cardGalleryImages;

    const source = card.querySelector('[data-card-gallery-images]');
    if (!source) return [];

    try {
      card.cardGalleryImages = JSON.parse(source.textContent).filter((image) => image && image.src);
    } catch (error) {
      card.cardGalleryImages = [];
    }

    const mediaItem = card.querySelector('[data-card-gallery-current-image]');
    const currentImage = card.querySelector('[data-card-gallery-image]');
    const currentPath = normalizeImagePath(currentImage && (currentImage.currentSrc || currentImage.src));

    if (currentPath && !card.cardGalleryImages.some((image) => normalizeImagePath(image.src) === currentPath)) {
      card.cardGalleryImages.unshift({
        id: mediaItem?.dataset.cardGalleryCurrentImage || currentPath,
        src: currentImage.currentSrc || currentImage.src,
        srcset: currentImage.srcset || '',
        alt: currentImage.alt || '',
        width: currentImage.naturalWidth || currentImage.width,
        height: currentImage.naturalHeight || currentImage.height
      });
    }

    return card.cardGalleryImages;
  }

  function imageUrlWithWidth(src, width) {
    try {
      const url = new URL(src, document.baseURI);
      url.searchParams.set('width', String(width));
      return url.toString();
    } catch (error) {
      return src;
    }
  }

  function galleryImageFromMedia(media) {
    const preview = media?.preview_image || media;
    if (!preview?.src) return null;

    const width = Number(preview.width) || 0;
    const responsiveWidths = [165, 360, 533, 720, 900, 1100].filter((candidate) => !width || candidate <= width);

    return {
      id: media.id || preview.id || preview.src,
      src: imageUrlWithWidth(preview.src, 370),
      srcset: responsiveWidths.map((candidate) => `${imageUrlWithWidth(preview.src, candidate)} ${candidate}w`).join(', '),
      alt: media.alt || preview.alt || '',
      width,
      height: Number(preview.height) || 0
    };
  }

  function warmCardGallery(card) {
    if (!card) return Promise.resolve([]);
    if (card.cardGalleryLoaded) return Promise.resolve(getGalleryImages(card));
    if (card.cardGalleryPromise) return card.cardGalleryPromise;

    const source = card.querySelector('[data-card-gallery-images]');
    const url = source?.dataset.cardGalleryUrl;
    if (!url || typeof window.fetch !== 'function') return Promise.resolve(getGalleryImages(card));

    card.cardGalleryPromise = window.fetch(url, { credentials: 'same-origin', headers: { Accept: 'application/json' } })
      .then((response) => {
        if (!response.ok) throw new Error(`Product gallery request failed: ${response.status}`);
        return response.json();
      })
      .then((product) => {
        const images = (product.media || [])
          .filter((media) => media && media.media_type === 'image')
          .map(galleryImageFromMedia)
          .filter(Boolean);

        if (images.length) {
          card.cardGalleryImages = images;
          source.textContent = JSON.stringify(images);
        }
        card.cardGalleryLoaded = true;
        return getGalleryImages(card);
      })
      .catch(() => getGalleryImages(card));

    return card.cardGalleryPromise;
  }

  function getVariantImageIds(card) {
    if (card.cardGalleryVariantImageIds) return card.cardGalleryVariantImageIds;

    const source = card.querySelector('[data-card-gallery-variant-image-ids]');
    if (!source) return [];

    try {
      card.cardGalleryVariantImageIds = JSON.parse(source.textContent).map(String);
    } catch (error) {
      card.cardGalleryVariantImageIds = [];
    }

    return card.cardGalleryVariantImageIds;
  }

  function normalizeImagePath(value) {
    if (!value) return '';

    try {
      return new URL(value, document.baseURI).pathname;
    } catch (error) {
      return value.split('?')[0];
    }
  }

  function getCurrentImageIndex(card, images) {
    const mediaItem = card.querySelector('[data-card-gallery-current-image]');
    const image = card.querySelector('[data-card-gallery-image]');
    const currentId = mediaItem && mediaItem.dataset.cardGalleryCurrentImage;
    let currentIndex = images.findIndex((item) => String(item.id) === String(currentId));

    if (currentIndex >= 0 || !image) return currentIndex;

    const currentPath = normalizeImagePath(image.currentSrc || image.src);
    return images.findIndex((item) => normalizeImagePath(item.src) === currentPath);
  }

  function getActiveGalleryImages(card) {
    const images = getGalleryImages(card);
    const variantImageIds = new Set(getVariantImageIds(card));
    const currentIndex = getCurrentImageIndex(card, images);
    if (images.length < 2 || !variantImageIds.size || currentIndex < 0) return images;

    let startIndex = currentIndex;
    while (startIndex > 0 && !variantImageIds.has(String(images[startIndex].id))) {
      startIndex -= 1;
    }

    let endIndex = startIndex + 1;
    while (endIndex < images.length && !variantImageIds.has(String(images[endIndex].id))) {
      endIndex += 1;
    }

    return images.slice(startIndex, endIndex);
  }

  function setCardImage(card, nextImage, animateDirection) {
    const mediaItem = card.querySelector('[data-card-gallery-current-image]');
    const image = card.querySelector('[data-card-gallery-image]');
    if (!mediaItem || !image || !nextImage) return;

    image.src = nextImage.src;
    image.srcset = nextImage.srcset || '';
    image.alt = nextImage.alt || '';
    if (nextImage.width) image.width = nextImage.width;
    if (nextImage.height) image.height = nextImage.height;
    mediaItem.dataset.cardGalleryCurrentImage = String(nextImage.id);

    if (
      animateDirection &&
      image.animate &&
      !window.matchMedia('(prefers-reduced-motion: reduce)').matches
    ) {
      image.animate(
        [
          { opacity: 0.72, transform: `translateX(${animateDirection > 0 ? '5%' : '-5%'})` },
          { opacity: 1, transform: 'translateX(0)' }
        ],
        { duration: 180, easing: 'cubic-bezier(0.2, 0.8, 0.2, 1)' }
      );
    }
  }

  function updateCardImageFromCard(card, direction, animate) {
    if (!card) return;

    const images = getActiveGalleryImages(card);
    if (images.length < 2) return;

    let currentIndex = getCurrentImageIndex(card, images);
    if (currentIndex < 0) currentIndex = 0;

    const nextImage = images[(currentIndex + direction + images.length) % images.length];
    setCardImage(card, nextImage, animate ? direction : 0);
  }

  function updateCardImage(button) {
    const card = button.closest('product-info, .product-card-wrapper');
    const direction = button.dataset.cardGalleryDirection === 'prev' ? -1 : 1;
    updateCardImageFromCard(card, direction);
  }

  function hasCoarsePointer() {
    return window.matchMedia && window.matchMedia('(pointer: coarse)').matches;
  }

  function getCardWrapper(card) {
    if (!card) return null;
    if (card.classList && card.classList.contains('product-card-wrapper')) return card;
    return card.querySelector('.product-card-wrapper');
  }

  function revealCardGallery(wrapper, blockNextMediaClick) {
    if (hasCoarsePointer()) return;
    if (!wrapper || !wrapper.querySelector('[data-card-gallery-images]')) return;

    wrapper.classList.add('is-card-gallery-visible');
    if (blockNextMediaClick) wrapper.dataset.cardGalleryBlockClick = 'true';

    window.clearTimeout(wrapper.cardGalleryTimer);
    wrapper.cardGalleryTimer = window.setTimeout(function () {
      wrapper.classList.remove('is-card-gallery-visible');
      delete wrapper.dataset.cardGalleryBlockClick;
    }, 3500);
  }

  function captureCardImage(card) {
    const mediaItem = card.querySelector('[data-card-gallery-current-image]');
    const image = card.querySelector('[data-card-gallery-image]');
    if (!mediaItem || !image) return null;

    return {
      id: mediaItem.dataset.cardGalleryCurrentImage || '',
      src: image.getAttribute('src') || '',
      srcset: image.getAttribute('srcset') || '',
      alt: image.getAttribute('alt') || '',
      width: image.getAttribute('width') || '',
      height: image.getAttribute('height') || ''
    };
  }

  function restoreCardImage(card) {
    const state = card.cardGalleryHoverRestore;
    const mediaItem = card.querySelector('[data-card-gallery-current-image]');
    const image = card.querySelector('[data-card-gallery-image]');
    if (!state || !mediaItem || !image) return;

    image.setAttribute('src', state.src);
    image.setAttribute('srcset', state.srcset);
    image.setAttribute('alt', state.alt);
    if (state.width) image.setAttribute('width', state.width);
    if (state.height) image.setAttribute('height', state.height);
    mediaItem.dataset.cardGalleryCurrentImage = state.id;
    delete card.cardGalleryHoverRestore;
    delete card.cardGalleryHoverImages;
    delete card.cardGalleryHoverIndex;
  }

  document.addEventListener('pointermove', function (event) {
    if (event.pointerType !== 'mouse' || hasCoarsePointer()) return;
    if (!(event.target instanceof Element)) return;

    const mediaLink = event.target.closest('.dt-collection-grid-shell .card__media a');
    if (!mediaLink) return;

    const card = getCardWrapper(mediaLink.closest('product-info, .product-card-wrapper'));
    if (!card) return;

    warmCardGallery(card);

    if (!card.cardGalleryHoverRestore) {
      card.cardGalleryHoverRestore = captureCardImage(card);
      card.cardGalleryHoverImages = getActiveGalleryImages(card);
    }

    const images = card.cardGalleryHoverImages || [];
    const bounds = mediaLink.getBoundingClientRect();
    if (images.length < 2 || bounds.width <= 0) return;

    const progress = Math.max(0, Math.min(0.9999, (event.clientX - bounds.left) / bounds.width));
    const nextIndex = Math.floor(progress * images.length);
    if (card.cardGalleryHoverIndex === nextIndex) return;

    card.cardGalleryHoverIndex = nextIndex;
    setCardImage(card, images[nextIndex], 0);
  }, { passive: true });

  document.addEventListener('pointerout', function (event) {
    if (event.pointerType !== 'mouse' || !(event.target instanceof Element)) return;

    const mediaLink = event.target.closest('.dt-collection-grid-shell .card__media a');
    if (!mediaLink || (event.relatedTarget instanceof Node && mediaLink.contains(event.relatedTarget))) return;

    const card = getCardWrapper(mediaLink.closest('product-info, .product-card-wrapper'));
    if (card) restoreCardImage(card);
  }, { passive: true });

  let cardGallerySwipe = null;

  document.addEventListener('pointerdown', function (event) {
    if (!(event.target instanceof Element)) return;
    if (event.pointerType === 'mouse') return;
    if (event.target.closest('[data-card-gallery-direction]')) return;

    const mediaLink = event.target.closest('.card__media a');
    if (!mediaLink) return;

    const wrapper = getCardWrapper(mediaLink.closest('product-info, .product-card-wrapper'));
    if (!wrapper) return;

    warmCardGallery(wrapper);

    cardGallerySwipe = {
      wrapper,
      startX: event.clientX,
      startY: event.clientY,
      pointerId: event.pointerId,
      didSwipe: false
    };
  }, { passive: true });

  document.addEventListener('pointermove', function (event) {
    if (!cardGallerySwipe || event.pointerId !== cardGallerySwipe.pointerId) return;

    const movedX = event.clientX - cardGallerySwipe.startX;
    const movedY = event.clientY - cardGallerySwipe.startY;
    const absX = Math.abs(movedX);
    const absY = Math.abs(movedY);
    if (cardGallerySwipe.didSwipe || absX < 34 || absX < absY * 1.25) return;

    cardGallerySwipe.didSwipe = true;
    updateCardImageFromCard(cardGallerySwipe.wrapper, movedX < 0 ? 1 : -1, true);
    cardGallerySwipe.wrapper.dataset.cardGalleryBlockClick = 'true';
    event.preventDefault();
  }, { passive: false });

  document.addEventListener('pointerup', function (event) {
    if (!cardGallerySwipe || event.pointerId !== cardGallerySwipe.pointerId) return;
    cardGallerySwipe = null;
  }, { passive: true });

  document.addEventListener('pointercancel', function (event) {
    if (!cardGallerySwipe || event.pointerId !== cardGallerySwipe.pointerId) return;
    cardGallerySwipe = null;
  }, { passive: true });

  document.addEventListener('change', function (event) {
    if (!(event.target instanceof Element) || !event.target.matches('.wc_selector_operation')) return;

    const input = event.target;
    if (input.disabled || input.getAttribute('aria-disabled') === 'true') return;

    const productInfo = input.closest('product-info');
    if (!productInfo) return;

    const card = getCardWrapper(productInfo);
    if (!card) return;

    const imageUrl = input.dataset.variantImage;
    const variantId = input.dataset.variantId;
    const variantMediaId = input.dataset.variantMediaId;
    const variantTitle = input.value || input.getAttribute('aria-label') || '';
    const productForm = productInfo.querySelector('product-form');
    const cardImage = card.querySelector('[data-card-gallery-image], .card__media img');
    const mediaItem = card.querySelector('[data-card-gallery-current-image]');

    if (imageUrl && cardImage) {
      cardImage.src = imageUrl;
      cardImage.srcset = imageUrl;
      if (mediaItem && variantMediaId) mediaItem.dataset.cardGalleryCurrentImage = variantMediaId;
    }

    if (imageUrl && productForm) {
      const productTitle = productForm.dataset.productTitle || '';
      productForm.dataset.productImage = imageUrl;
      productForm.dataset.productImageAlt = variantTitle ? `${productTitle} ${variantTitle}`.trim() : productTitle;
    }

    const cardLink = card.querySelector('.card__media a');
    if (cardLink && variantId) {
      const baseUrl = (cardLink.getAttribute('href') || '').split('?')[0];
      if (baseUrl) {
        const variantUrl = `${baseUrl}?variant=${variantId}`;
        cardLink.setAttribute('href', variantUrl);
        if (productForm) productForm.dataset.productUrl = variantUrl;
      }
    }

    if (variantId) {
      const variantInput = productInfo.querySelector('input.product-variant-id');
      const addButton = productInfo.querySelector('[name="add"]');
      if (variantInput) {
        variantInput.value = variantId;
        variantInput.removeAttribute('disabled');
      }
      if (addButton) addButton.disabled = false;
    }

    if (productForm && variantTitle) {
      productForm.dataset.productVariantTitle = variantTitle;
      productForm.dataset.productVariantOptionName = input.name && input.name.toLowerCase().includes('color') ? 'Color' : '';
    }

    warmCardGallery(card);

    window.requestAnimationFrame(function () {
      const images = getGalleryImages(card);
      const image = card.querySelector('[data-card-gallery-image]');
      const mediaItem = card.querySelector('[data-card-gallery-current-image]');
      const currentPath = normalizeImagePath(image && (image.currentSrc || image.src));
      const currentImage = images.find((item) => normalizeImagePath(item.src) === currentPath);

      if (currentImage && mediaItem) {
        mediaItem.dataset.cardGalleryCurrentImage = String(currentImage.id);
      }

      delete card.cardGalleryHoverRestore;
      delete card.cardGalleryHoverImages;
      delete card.cardGalleryHoverIndex;
    });
  });

  document.addEventListener('click', function (event) {
    if (!(event.target instanceof Element)) return;
    const button = event.target.closest('[data-card-gallery-direction]');
    if (button) {
      if (hasCoarsePointer()) return;
      event.preventDefault();
      event.stopPropagation();
      updateCardImage(button);
      revealCardGallery(getCardWrapper(button.closest('product-info, .product-card-wrapper')), false);
      return;
    }

    const mediaLink = event.target.closest('.card__media a');
    if (!mediaLink) return;

    const wrapper = getCardWrapper(mediaLink.closest('product-info, .product-card-wrapper'));
    if (wrapper && wrapper.dataset.cardGalleryBlockClick === 'true') {
      event.preventDefault();
      event.stopPropagation();
      delete wrapper.dataset.cardGalleryBlockClick;
    }
  });
})();




// Product card blank-area click-through on collection grids
(function () {
  if (window.productCardClickThroughInitialized) return;
  window.productCardClickThroughInitialized = true;

  const interactiveSelector = [
    'a',
    'button',
    'input',
    'label',
    'select',
    'textarea',
    'modal-opener',
    'modal-dialog',
    '[role="button"]',
    '[tabindex]:not([tabindex="-1"])',
    '.dt-card-wishlist',
    '.wishlist-card-button',
    '[data-wishlist-card-button]',
    '.slider-button'
  ].join(',');

  let touchCardTap = null;

  function isModifiedClick(event) {
    return event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;
  }

  function isCardInteractiveTarget(target) {
    return target.closest(interactiveSelector);
  }

  function getCardFromPoint(x, y) {
    if (!document.elementsFromPoint) return null;

    const elements = document.elementsFromPoint(x, y);
    for (const element of elements) {
      const card = element.closest && element.closest('product-info.card-main-wrapper[data-layout="card"][data-card-url]');
      if (card) return card;
    }

    for (const element of elements) {
      const item = element.closest && element.closest('.collection-products .product, .collection-products .grid__item, .product-grid .grid__item');
      const card = item && item.querySelector('product-info.card-main-wrapper[data-layout="card"][data-card-url]');
      if (card) return card;
    }

    return null;
  }

  function getCardFromEvent(event) {
    const targetCard = event.target.closest('product-info.card-main-wrapper[data-layout="card"][data-card-url]');
    return targetCard || getCardFromPoint(event.clientX, event.clientY);
  }

  function showPdpTransition(cardUrl) {
    if (typeof window.dtShowPdpTransition === 'function') {
      window.dtShowPdpTransition(cardUrl);
    } else {
      try {
        if (window.sessionStorage && cardUrl.indexOf('/products/') === 0) {
          sessionStorage.setItem('dt:pdpTransition', '1');
        }
      } catch (error) {}
    }
  }

  function openCardPdp(card) {
    const cardUrl = card && card.dataset.cardUrl;
    if (!cardUrl) return;

    showPdpTransition(cardUrl);
    window.location.href = cardUrl;
  }

  document.addEventListener('pointerdown', function (event) {
    if (!(event.target instanceof Element) || event.pointerType === 'mouse') return;

    const card = getCardFromEvent(event);
    if (!card || isCardInteractiveTarget(event.target)) {
      touchCardTap = null;
      return;
    }

    touchCardTap = {
      card,
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY
    };
  }, { passive: true });

  document.addEventListener('pointerup', function (event) {
    if (!touchCardTap || event.pointerId !== touchCardTap.pointerId) return;

    const tap = touchCardTap;
    touchCardTap = null;

    if (!(event.target instanceof Element)) return;
    if (getCardFromEvent(event) !== tap.card || isCardInteractiveTarget(event.target)) return;

    const movedX = Math.abs(event.clientX - tap.startX);
    const movedY = Math.abs(event.clientY - tap.startY);
    if (movedX > 8 || movedY > 8) return;

    event.preventDefault();
    event.stopPropagation();
    openCardPdp(tap.card);
  }, { passive: false });

  document.addEventListener('pointercancel', function (event) {
    if (!touchCardTap || event.pointerId !== touchCardTap.pointerId) return;
    touchCardTap = null;
  }, { passive: true });

  document.addEventListener('click', function (event) {
    if (event.defaultPrevented || isModifiedClick(event)) return;
    if (!(event.target instanceof Element)) return;

    const card = getCardFromEvent(event);
    if (!card || isCardInteractiveTarget(event.target)) return;

    openCardPdp(card);
  });
})();


// page scroll

// page scroll
document.addEventListener('DOMContentLoaded', function () {
  function updateProgressBar() {
    const progressBar = document.querySelector('.indicatorebar_inner');
    if (progressBar) {
      // Use requestAnimationFrame to decouple read/write and prevent layout thrashing
      window.requestAnimationFrame(() => {
        const totalHeight = document.body.scrollHeight - window.innerHeight;
        const progress = (window.pageYOffset / totalHeight) * 100;
        progressBar.style.width = progress + '%';
      });
    }
  }

  // Throttle scroll updates using requestAnimationFrame
  let ticking = false;
  window.addEventListener('scroll', function () {
    if (!ticking) {
      window.requestAnimationFrame(function () {
        updateProgressBar();
        ticking = false;
      });
      ticking = true;
    }
  }, { passive: true });

  // Debounce resize updates
  window.addEventListener('resize', debounce(updateProgressBar, 150));

  // Initial call
  updateProgressBar();
});

// page scrollbar width - Debounced to prevent layout thrashing on resize
function updateScrollbarWidth() {
  const scrollbar = window.innerWidth - document.documentElement.clientWidth;
  document.documentElement.style.setProperty('--scrollbar-width', `${scrollbar}px`);
}

const debouncedUpdateScrollbarWidth = debounce(updateScrollbarWidth, 200);

document.addEventListener('DOMContentLoaded', updateScrollbarWidth);
window.addEventListener('resize', debouncedUpdateScrollbarWidth);
